import { Trans } from '@lingui/react';
import { Document } from '@startuptools/business-ui/document/Document';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import {
  AoaStepDocumentDocument,
  useAoaStepDocumentLazyQuery,
  useAoaStepDocumentSuspenseQuery,
} from './StepDocument.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { apiClient } from '../../../../common/http';
import { useDocGenAoaRegistrationFormMutation } from '../../../../graphql/react-operations';
import { GqlDocSystemTagKind, GqlDocumentSignatureMethod } from '../../../../graphql/base-types.graphql';
import { Divider } from '@startuptools/ui/joy/Divider';
import { DocumentSignatures } from '../../../../components/document-signatures/DocumentSignatures';
import { useCompanySubscriptionSuspenseQuery } from '../../../../components/side-nav/CompanySubscription.graphql';
import { MeetingMinutesDocument } from '../../../common/components/documents/MeetingMinutes';
import { useMeetingHasQesRegFormsSuspenseQuery } from '../../../../graphql/react-operations';
export const StepDocument = ({ id }: { id: string }) => {
  const companyId = useCompanyId();
  const { data } = useAoaStepDocumentSuspenseQuery({ variables: { id, companyId } });
  const { data: { companySubscription } = { companySubscription: null } } = useCompanySubscriptionSuspenseQuery({
    variables: { companyId },
  });
  const { data: { meetingHasQesRegForms } = { meetingHasQesRegForms: false } } = useMeetingHasQesRegFormsSuspenseQuery({
    variables: { companyId, input: { id: data.articlesOfAssoc.meetingConcern?.meetingId ?? '', skipId: id } },
    skip: !data.articlesOfAssoc.meetingConcern?.meetingId,
    fetchPolicy: 'no-cache',
  });
  const [fetchAoaStepDocument] = useAoaStepDocumentLazyQuery({ fetchPolicy: 'network-only' });
  // eslint-disable-next-line sut/loading-mutation
  const [generateForm] = useDocGenAoaRegistrationFormMutation({ refetchQueries: [AoaStepDocumentDocument] });

  const companyCanUseProFeatures = companySubscription?.canUseProFeatures ?? false;

  return (
    <Stack gap={1}>
      <Typography level="h3">
        <Trans id="Document" />
      </Typography>
      <Document
        document={data.articlesOfAssoc.latestDocument}
        documentSystemTagKind={GqlDocSystemTagKind.ArticlesOfAssocRegForm}
        defaultFileName={data.articlesOfAssoc.defaultFileName}
        hasDigitalRegistrationForms={meetingHasQesRegForms}
        validate={async () => {
          const res = await fetchAoaStepDocument({ variables: { id, companyId } });
          if (!res.data) {
            throw new Error('No data');
          }
          const errors = Object.values(res.data.articlesOfAssocErrors);
          return {
            isValid: !errors.some(e => e.hasError),
            errors: Object.values(res.data.articlesOfAssocErrors),
          };
        }}
        preview={() => apiClient.previewAoaRegistrationForm(companyId, id)}
        generate={signatureMethod =>
          generateForm({
            variables: {
              companyId,
              aoaId: id,
              signatureMethod: signatureMethod ?? GqlDocumentSignatureMethod.Analogue,
            },
          })
        }
        allowDigitalSignature={companyCanUseProFeatures}
        canUseProFeatures={companyCanUseProFeatures}
        signaturesSlot={
          data.articlesOfAssoc.latestDocument?.signatureMethod === GqlDocumentSignatureMethod.Digital &&
          companyCanUseProFeatures && (
            <>
              <Divider />
              <DocumentSignatures
                documentId={data.articlesOfAssoc.latestDocument.id}
                scriveStatus={data.articlesOfAssoc.latestDocument.scriveStatus}
                zignedAgreementStatus={data.articlesOfAssoc.latestDocument.zignedAgreementStatus}
                signAllowedAction={data.articlesOfAssoc.latestDocument.signAllowedAction}
                isQualifiedEsign
              />
            </>
          )
        }
      />
      {data.articlesOfAssoc.meetingConcern?.meeting && (
        <MeetingMinutesDocument meeting={data.articlesOfAssoc.meetingConcern.meeting} />
      )}
    </Stack>
  );
};
